import * as THREE from 'three'
import Experience from '../Experience.js'
import TravelBall from './TravelBall.js'

/**
 * World
 * ─────────────────────────────────────────────────────────────────
 * Replaces the default test mesh with a 2 000-point particle sphere.
 *
 * Architecture notes
 * ──────────────────
 * - Uses the Experience singleton pattern: new Experience() returns
 *   the already-created instance so we can access all subsystems.
 * - Listens to time.on('tick') for per-frame updates.
 * - Does NOT instantiate Environment — no scene lighting needed for
 *   PointsMaterial (emissive-style via AdditiveBlending).
 * - Renderer clear-color is set here to match the page background.
 *
 * WebGL Design
 * ────────────
 * - 2 000 points, BufferGeometry, PointsMaterial
 * - Particle texture: soft radial gradient (canvas 2D → CanvasTexture)
 *   so each point looks like a glowing disc, not a hard square pixel.
 * - Vertex colours: linear mix of #6C63FF (accent) and #00D4FF (secondary)
 * - Per-tick: sin/cos deformation on x and y axes creates a slow
 *   organic "breathing" motion around the initial sphere positions.
 * - Mouse parallax: smoothly lerps particle-group rotation toward
 *   normalised cursor position for a subtle, physical feel.
 *   (Spring-like lerp, per emilkowalski decorative-motion guidance.)
 */
export default class World {
    constructor() {
        // ── Core references ──────────────────────────────────────
        this.experience = new Experience()
        this.scene      = this.experience.scene
        this.resources  = this.experience.resources
        this.time       = this.experience.time

        // ── Mouse state ──────────────────────────────────────────
        // current  — smoothed value used for rotation
        // target   — raw normalised value set on mousemove
        this.mouse = { x: 0, y: 0 }
        this.mouseTarget = { x: 0, y: 0 }

        // ── Background colour ────────────────────────────────────
        // Must match --bg: #080810 from style.css so the canvas
        // blends seamlessly into the page (Renderer.js uses opaque
        // WebGLRenderer — no alpha: true support without modifying it).
        this.experience.renderer.instance.setClearColor(0x080810, 1)

        // ── Bootstrap ────────────────────────────────────────────
        // sources.js is empty, so 'ready' fires on the next tick.
        this.resources.on('ready', () => {
            this._buildParticleTexture()
            this._buildParticles()
            this._initMouse()
            // TravelBall creates its own overlay renderer + scene.
            // It only borrows the particle texture for visual consistency.
            this.travelBall = new TravelBall(this.particleTexture)
        })

        this.time.on('tick', () => this._update())
    }

    /* ────────────────────────────────────────────────────────────
       Soft radial-gradient texture — turns square gl_PointSize
       fragments into round, glowing discs.
    ──────────────────────────────────────────────────────────── */
    _buildParticleTexture() {
        const size = 64
        const c    = document.createElement('canvas')
        c.width    = size
        c.height   = size
        const ctx  = c.getContext('2d')

        const half = size / 2
        const grad = ctx.createRadialGradient(half, half, 0, half, half, half)
        grad.addColorStop(0.00, 'rgba(255,255,255,1.00)')
        grad.addColorStop(0.25, 'rgba(255,255,255,0.85)')
        grad.addColorStop(0.65, 'rgba(255,255,255,0.20)')
        grad.addColorStop(1.00, 'rgba(255,255,255,0.00)')

        ctx.fillStyle = grad
        ctx.fillRect(0, 0, size, size)

        this.particleTexture = new THREE.CanvasTexture(c)
    }

    /* ────────────────────────────────────────────────────────────
       2 000-point sphere
    ──────────────────────────────────────────────────────────── */
    _buildParticles() {
        const COUNT = 2000

        const positions = new Float32Array(COUNT * 3)
        const colors    = new Float32Array(COUNT * 3)

        const colorA  = new THREE.Color('#6C63FF') // accent
        const colorB  = new THREE.Color('#00D4FF') // secondary
        const scratch = new THREE.Color()

        for (let i = 0; i < COUNT; i++) {
            const i3 = i * 3

            /* Spherical distribution — uniform on surface area.
               phi  ∈ [0, π] via acos keeps density even at poles.
               r    slightly varied to create a 'cloud' depth.     */
            const theta = Math.random() * Math.PI * 2
            const phi   = Math.acos(2 * Math.random() - 1)
            const r     = 1.85 + (Math.random() - 0.5) * 0.65

            positions[i3 + 0] = r * Math.sin(phi) * Math.cos(theta)
            positions[i3 + 1] = r * Math.cos(phi)
            positions[i3 + 2] = r * Math.sin(phi) * Math.sin(theta)

            /* Vertex colour: lerp between accent and secondary. */
            scratch.copy(colorA).lerp(colorB, Math.random())
            colors[i3 + 0] = scratch.r
            colors[i3 + 1] = scratch.g
            colors[i3 + 2] = scratch.b
        }

        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color',    new THREE.BufferAttribute(colors,    3))

        /* PointsMaterial
           size 0.07 — in world units with sizeAttenuation, renders as
           ~4-6px soft discs at the default camera distance (~10.8 units).
           AdditiveBlending + depthWrite:false → overlapping particles
           accumulate brightness, creating organic cluster glow.          */
        const material = new THREE.PointsMaterial({
            size:            0.07,
            map:             this.particleTexture,
            vertexColors:    true,
            transparent:     true,
            opacity:         0.88,
            blending:        THREE.AdditiveBlending,
            depthWrite:      false,
            sizeAttenuation: true,
        })

        this.particles = new THREE.Points(geometry, material)
        this.scene.add(this.particles)

        /* Store a snapshot of the original positions so the wave
           deformation never drifts — we always offset from rest. */
        this.initialPositions = Float32Array.from(positions)
    }

    /* ────────────────────────────────────────────────────────────
       Mouse tracking
       Uses document.documentElement.clientWidth/Height to get the
       real viewport size, bypassing the window.innerWidth patch in
       script.js (which returns the canvas-container width).
    ──────────────────────────────────────────────────────────── */
    _initMouse() {
        window.addEventListener('mousemove', (e) => {
            const vw = document.documentElement.clientWidth
            const vh = document.documentElement.clientHeight
            this.mouseTarget.x =  (e.clientX / vw) * 2 - 1
            this.mouseTarget.y = -(e.clientY / vh) * 2 + 1
        }, { passive: true })
    }

    /* ────────────────────────────────────────────────────────────
       Per-frame update — called via time.on('tick')
    ──────────────────────────────────────────────────────────── */
    _update() {
        if (!this.particles) return

        // TravelBall runs its own per-frame logic (position lerp + colours).
        if (this.travelBall) this.travelBall.update()

        const t    = this.time.elapsed * 0.001  // seconds
        const attr = this.particles.geometry.attributes.position
        const init = this.initialPositions

        /* ── Wave deformation ───────────────────────────────────
           Each particle oscillates around its rest position using
           sin/cos of (time + position-derived phase). The offset is
           small (~0.06-0.09 units) so the sphere breathes without
           losing its recognisable shape.

           Two independent waves on different axes + slightly
           different frequencies give it an organic, non-mechanical
           feel (emilkowalski spring-physics philosophy applied to
           procedural motion).                                       */
        for (let i = 0; i < 2000; i++) {
            const i3 = i * 3
            const ox = init[i3 + 0]
            const oy = init[i3 + 1]
            const oz = init[i3 + 2]

            attr.array[i3 + 1] = oy + Math.sin(t * 0.55 + ox * 1.7 + oz * 0.9) * 0.075
            attr.array[i3 + 0] = ox + Math.cos(t * 0.42 + oy * 2.1)             * 0.038
        }
        attr.needsUpdate = true

        /* ── Mouse parallax (spring lerp) ───────────────────────
           Factor 0.04 → ~40 frame settling time at 60 fps ≈ 0.67 s.
           Feels weighted / physical without a real physics library.
           Influence capped at ±0.5 rad and ±0.35 rad to stay subtle.  */
        this.mouse.x += (this.mouseTarget.x - this.mouse.x) * 0.04
        this.mouse.y += (this.mouseTarget.y - this.mouse.y) * 0.04

        /* Combine slow auto-rotation with cursor-driven parallax.
           Auto-rotation speed 0.055 rad/s ≈ one full turn per ~114 s. */
        this.particles.rotation.y = t * 0.055 + this.mouse.x * 0.50
        this.particles.rotation.x =             this.mouse.y * 0.34
    }
}
